import { gsap } from 'gsap';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';
import { vec2 } from 'gl-matrix';
import mapToRange from './mapToRange';
import { getTheme } from './themes';

/**
 * Background elements for the homepage interation
 *
 * @class    BG
 * @param    {object}  options  Options for the object
 * @return   {object}  The object
 */
const BG = function BG(options) {
  gsap.registerPlugin(MorphSVGPlugin);

  //
  //   Public Vars
  //
  //////////////////////////////////////////////////////////////////////

  const self = {
    app: null,
    parent: null,
    x: 0,
    y: 0,
    targetBaseX: 0,
    targetBaseY: 0,
    targetReachX: 0,
    targetReachY: 0,
    isPaused: true,
    reduceMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    settings: {
      duration: 3,
      bgRotateSpeed: 0.001,
      bgReachRestrict: 0,
    },
    ...options,
  };

  //
  //   Private Vars
  //
  //////////////////////////////////////////////////////////////////////

  const canvas = document.querySelector('[data-bg-canvas]');
  const ctx = canvas.getContext('2d');
  const dpr = 1;
  const smallShapeScale = 4;
  const largeShapeScale = 2.3;
  let shapeScale = largeShapeScale;
  let vw;
  let vh;

  const graphicSize = 6000;

  let iter = 0;

  const paths = {};

  const blobA = `M471.4,739.4c381.9-667,1296.2-1122,2411.7-283.1c554.6,417,638,1118.1,338.9,1393.3s-810.3,70.7-1046.1-392.4
    c-245-481.2-643.7-569.7-687.6-208.6c-74,608,881,1304.7,1655.5,1267.6c521.9-25,1654.4-272.4,1814-1044.9
    c73.3-354.6-64.5-529.7-252-500.5c-281.5,43.8-213.9,536.2-523.5,593.5c-204.2,37.8-464.9-99.4-464.9-522.1
    c0-644.3,863.5-1108,1467.1-664.8c484.8,355.9,768.7,1182.6,249.2,1813.8c-515.4,626.2-2259.4,846.4-2737.4,1692.6
    c-311.5,551.4-109,915.9,258.3,950.5c314.4,29.6,522.6-262.7,736.5-741.5c322.6-722.1,834.6-1321.1,1629.3-1205.3
    c617,89.9,774.1,953.9,613.4,1618.2c-257.9,1065.8-1082.6,1492.7-1273.7,1137.2c-180.7-336.2,556.8-1016.1,601.7-1632.3
    c29.7-406.6-323.2-440.5-601.7,51.9c-382.3,675.8-535.3,1623.6-1485.7,1843.1c-599.2,138.4-1714.2,5.3-1714.2-1042.1
    S2249.1,3671,2249,3223.8c-0.1-405.6-361.1-515.3-685.6-607.8c-546-155.7-554.4,251.8-387.8,726.4
    c254.9,726.4-41.3,1784.7-697.4,1594.4C-51.2,4783.4,2.5,3451.6,2.5,2835.4C2.5,2125.9,132.8,1330.7,471.4,739.4z`;

  const blobB = `M471.4,739.4c381.9-667,1296.2-1122,2411.7-283.1c554.6,417,638,1118.1,338.9,1393.3s-931.1-60.9-1249.9-715.1
    c-331.3-679.8-870.3-804.8-929.8-294.7c-100.1,858.9,1138.3,1629.4,2101.5,1686.6c747.2,44.3,1834.6-196.3,2042.3-1150
    c95.3-437.8-83.9-654-327.9-618C4492,812.4,4580.6,1420.4,4177,1491c-220.6,38.6-475.4-128.7-452-550.5
    c36.1-651.8,929.9-1132.9,1533.5-689.7c484.8,355.9,768.7,1182.6,249.2,1813.8c-515.4,626.2-2371.2,732.8-3023.8,1842.3
    c-425.3,723-148.8,1201.1,352.6,1246.4c429.1,38.8,713.4-344.5,1005.4-972.5c440.4-947,1093-1707.9,1809.3-1309.9
    c472.8,262.7,389.7,926.4,196.3,1584.7c-308.3,1049.3-996.7,1542.4-1187.8,1186.9c-180.7-336.2,633.1-954.6,686.2-1750.1
    c35.1-524.9-382.3-568.7-711.7,67c-452.1,872.5-687.5,1622.7-1521.7,1821.4c-526,125.3-1504.7,4.8-1504.7-943.6
    S2249.1,3671,2249,3223.8c-0.1-405.6-263-626.3-833.2-729.7c-959.7-174-974.4,281.5-681.6,812.2
    c448.1,812.2,400.1,1820.7-256,1630.5C-51.2,4783.4,2.5,3451.6,2.5,2835.4C2.5,2125.9,132.8,1330.7,471.4,739.4z`;

  //
  //   Private Methods
  //
  //////////////////////////////////////////////////////////////////////

  const _init = () => {
    _createGui();
    _resize();
    _setup();
    _addEventListeners();
    _setGlobalMethods();
  };

  const _createGui = () => {
    if (!APP.devMode) {
      return;
    }
    APP.gui.add(self.settings, 'bgRotateSpeed', 0.0001, 0.01);
  };

  const _addEventListeners = () => {
    window.addEventListener('resize', _resize, true);
  };

  const _resize = () => {
    // eslint-disable-next-line no-multi-assign
    vw = canvas.width = window.innerWidth * dpr;
    // eslint-disable-next-line no-multi-assign
    vh = canvas.height = window.innerHeight * dpr;
    canvas.setAttribute('width', vw);
    canvas.setAttribute('height', vh);

    shapeScale = APP.isMqSmall() ? smallShapeScale : largeShapeScale;
  };

  const _setup = () => {
    _createBlob(0, {
      x: 700,
      y: -300,
      rotation: 0.3,
      scale: 1.3,
    });

    gsap.ticker.add(_onTick);
  };

  // eslint-disable-next-line no-shadow
  const _createBlob = (idx, options) => {
    // Object to animate
    const path = {
      d: blobA,
    };

    const tl = gsap.timeline({
      repeat: -1,
      yoyo: true,
      onUpdate: () => {
        paths[idx] = {
          path,
          options,
        };
      },
      onUpdateParams: [path, options],
    });

    tl.to(path, self.settings.duration, {
      d: blobB,
      stringFilter: MorphSVGPlugin.pathFilter,
      ease: 'sine.inOut',
    });
  };

  const _onTick = () => {
    if (self.isPaused) {
      return;
    }
    if (iter > 1 && APP.reducedMotion) return;
    // eslint-disable-next-line no-plusplus
    iter++;
    _draw();
  };

  const _getShapesScale = () => ({
    w: vw * shapeScale,
    h: vh * shapeScale,
  });

  const _draw = () => {
    ctx.clearRect(0, 0, vw, vh);

    ctx.fillStyle = getTheme().bgFill;
    ctx.strokeStyle = getTheme().bgStroke;

    // Draw bg
    ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);

    // Get oscillation offsets
    const effectX =
      APP.settings.bgOscAmp *
      Math.sin(Math.PI * 2 * (iter / APP.settings.bgOscPeriod));
    const effectY =
      APP.settings.bgOscAmp *
      Math.cos(Math.PI * 2 * (iter / APP.settings.bgOscPeriod));

    // Draw metaballs
    ctx.save();

    // eslint-disable-next-line no-shadow
    const shapeScale = _getShapesScale();
    const offsetX = (shapeScale.w - vw) / 2;
    const offsetY = (shapeScale.h - vh) / 2;
    ctx.translate(-self.x - offsetX + effectX, -self.y - offsetY + effectY);

    // Draw paths
    const pathKeys = Object.keys(paths);
    pathKeys.forEach((key) => {
      const item = paths[key];
      _drawPath(item.path, item.options);
    });

    ctx.restore();
  };

  // eslint-disable-next-line no-shadow
  const _drawPath = (path, options = {}) => {
    // Scale the graphic to fill the shape scale
    // eslint-disable-next-line no-shadow
    const shapeScale = _getShapesScale();
    const graphicScale = shapeScale.w / graphicSize;
    const translateX = shapeScale.w / 2 - graphicSize / 2;
    const translateY = shapeScale.h / 2 - graphicSize / 2;
    ctx.translate(translateX, translateY);
    ctx.translate(graphicSize / 2, graphicSize / 2);
    ctx.rotate(
      iter * self.settings.bgRotateSpeed * (APP.isMqSmall() ? 0.6 : 1)
    );
    ctx.scale(graphicScale, graphicScale);
    ctx.translate(-graphicSize / 2, -graphicSize / 2);

    // Scale the line width
    ctx.lineWidth = 0.9 / graphicScale;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.fill(new Path2D(path.d));
    ctx.globalCompositeOperation = 'source-over';
    ctx.stroke(new Path2D(path.d));
  };

  const _setGlobalMethods = () => {
    window.stopBg = self.stop;
    window.startBg = self.start;
    window.revealBg = self.reveal;
    window.restrictBgReach = self.restrictBgReach;
    window.restoreBgReach = self.restoreBgReach;
  };

  //
  //   Public Methods
  //
  //////////////////////////////////////////////////////////////////////

  self.stop = () => {
    self.isPaused = true;
    self.hide();
  };

  self.start = () => {
    self.isPaused = false;
    self.show();
  };

  self.setBgReach = (restrictAmount = 0) => {
    self.settings.bgReachRestrict = restrictAmount;
  };

  self.restoreBgReach = () => {
    self.settings.bgReachRestrict = 1;
  };

  self.reveal = () => {
    canvas.style.opacity = 1;
  };

  self.hide = () => {
    canvas.style.display = 'none';
  };

  self.show = () => {
    canvas.style.display = 'block';
  };

  self.update = () => {
    if (self.isPaused) return;
    if (iter > 1 && APP.reducedMotion) return;

    // Update base value
    self.targetBaseY = 0;
    self.targetBaseX = 0;

    // Get mouse distance from center
    const mouseVec = vec2.fromValues(
      APP.mouseX - window.innerWidth / 2,
      APP.mouseY - window.innerHeight / 2
    );
    const distance = vec2.length(mouseVec);

    // Update reach value
    const reachFraction = mapToRange(0, window.innerWidth, 0, 1, distance);
    const moveVec = vec2.clone(mouseVec);
    vec2.normalize(moveVec, moveVec);
    vec2.scale(moveVec, moveVec, reachFraction);
    vec2.scale(
      moveVec,
      moveVec,
      (_getShapesScale().w / 2) *
        (self.settings.bgReachRestrict * APP.settings.bgMaxReach)
    );
    // eslint-disable-next-line prefer-destructuring
    self.targetReachX = moveVec[0];
    // eslint-disable-next-line prefer-destructuring
    self.targetReachY = moveVec[1];

    // Update position
    const xChange =
      (self.targetBaseX + self.targetReachX - self.x) * APP.settings.bgEase;
    const yChange =
      (self.targetBaseY + self.targetReachY - self.y) * APP.settings.bgEase;

    self.x += xChange;
    self.y += yChange;

    // Draw
    _draw();
  };

  self.destroy = () => {};

  //
  //   Initialize
  //
  //////////////////////////////////////////////////////////////////////

  _init();

  // Return the Object
  return self;
};

export default BG;
